// src/pages/Consignacion/Tabs/TabInforme.js
//
// Informe del stock en consignación.
//
// El consumo sale de los traslados que salen del almacén: en consignación no se
// factura ni se hace salida de mercadería, el repuesto se mueve de CONSIG al
// taller que lo usa. Eso permite además saber QUÉ almacén consume cada cosa.
//
// Mientras el almacén no tenga movimientos, la mitad del informe no tiene qué
// mostrar. Se dice explícitamente en vez de llenar la pantalla de ceros.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { formatMoney, formatMoneyCorto, formatNumber } from '../../../utils/formato';
import '../Consignacion.css';

const RANGOS = [
  { id: '30d',  texto: 'Últimos 30 días' },
  { id: '90d',  texto: 'Últimos 90 días' },
  { id: '180d', texto: 'Últimos 6 meses' },
  { id: '365d', texto: 'Último año' }
];

// Verde para subir y rojo para bajar. El color va con la dirección del cambio
// sobre el stock, no con la urgencia: subir es agregar, bajar es sacar. La
// flecha acompaña al color para que se lea igual sin distinguirlos.
const SUGERENCIAS = {
  subir:         { texto: 'Subir',       clase: 'bg-success-subtle text-success-emphasis', icono: 'bi-arrow-up' },
  bajar:         { texto: 'Bajar',       clase: 'bg-danger-subtle text-danger-emphasis',   icono: 'bi-arrow-down' },
  'sin-consumo': { texto: 'Sin consumo', clase: 'bg-secondary-subtle text-secondary-emphasis', icono: 'bi-dash-circle' }
};

const formatFecha = (iso) => {
  if (!iso) return '—';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};

const Tarjeta = ({ titulo, valor, detalle, icono, color = 'primary' }) => (
  <div className="col-lg-3 col-md-6 mb-3">
    <div className="card shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex align-items-center gap-2 text-muted small text-uppercase fw-semibold">
          <i className={`bi ${icono} text-${color}`}></i>
          {titulo}
        </div>
        <div className="fs-3 fw-bold mt-2">{valor}</div>
        {detalle && <div className="text-muted small">{detalle}</div>}
      </div>
    </div>
  </div>
);

const TabInforme = () => {
  const [datos, setDatos] = useState(null);
  const [rango, setRango] = useState('90d');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async (rangoPedido) => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await api.get('/consignacion/informe', { params: { rango: rangoPedido } });
      setDatos(data);
    } catch (err) {
      console.error('Error cargando el informe:', err);
      setError(err.response?.data?.error || 'No se pudo cargar el informe de consignación.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(rango); }, [cargar, rango]);

  if (cargando) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2">Calculando el informe...</p>
      </div>
    );
  }

  if (error) return <div className="alert alert-danger mt-4">{error}</div>;
  if (!datos) return null;

  const { totales, periodo } = datos;

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <p className="text-muted small mb-0">
          <i className="bi bi-calendar3 me-1"></i>
          {formatFecha(periodo.desde)} — {formatFecha(periodo.hasta)} ({periodo.dias} días)
          <span className="mx-2">·</span>
          Almacén <strong>{datos.almacen}</strong>
        </p>
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto' }}
          value={rango}
          onChange={e => setRango(e.target.value)}
        >
          {RANGOS.map(r => <option key={r.id} value={r.id}>{r.texto}</option>)}
        </select>
      </div>

      {/* --- Situación: funciona siempre, no necesita movimientos --- */}
      <div className="row">
        <Tarjeta
          titulo="Valor del stock"
          valor={formatMoneyCorto(totales.valorizado)}
          detalle={`${formatMoney(totales.valorizado)} · ${formatNumber(totales.unidadesEnStock)} unidades`}
          icono="bi-cash-stack"
          color="success"
        />
        <Tarjeta
          titulo="Reposición pendiente"
          valor={formatMoneyCorto(totales.reposicionPendiente)}
          detalle="Lo que cuesta llevar todo a su stock mínimo"
          icono="bi-cart-plus"
          color="primary"
        />
        <Tarjeta
          titulo="Consumo del período"
          valor={formatNumber(totales.unidadesConsumidas)}
          detalle={`unidades salidas de ${datos.almacen}`}
          icono="bi-arrow-left-right"
          color="info"
        />
        <Tarjeta
          titulo="Sin movimiento"
          valor={formatNumber(totales.sinMovimiento)}
          detalle={`de ${totales.articulos} artículos`}
          icono="bi-pause-circle"
          color="secondary"
        />
      </div>

      {/* Sin movimientos, media pantalla no tiene nada que decir. Se avisa
          en vez de mostrar tablas vacías que parecen un error. */}
      {!datos.hayMovimientos && (
        <div className="alert alert-info d-flex align-items-start gap-2">
          <i className="bi bi-info-circle-fill mt-1"></i>
          <div>
            <strong>Todavía no hay consumo registrado en este período.</strong>
            <div className="small mt-1">
              El consumo de consignación se registra como un traslado del almacén {datos.almacen} al
              taller que usa el repuesto. Mientras no haya traslados, no se puede calcular rotación
              ni revisar si los stocks mínimos están bien puestos. Las tarjetas de arriba sí son
              válidas: salen del stock actual.
            </div>
          </div>
        </div>
      )}

      {/* Si hay stock de un artículo sin precio en la lista, valoriza 0 y el
          total queda corto. Mostrarlo sin avisar sería dar por exacto un
          número incompleto. */}
      {totales.conStockSinPrecio > 0 && (
        <div className="alert alert-warning d-flex align-items-start gap-2">
          <i className="bi bi-exclamation-triangle-fill mt-1"></i>
          <div className="small">
            <strong>{totales.conStockSinPrecio}</strong> artículo(s) tienen stock pero no tienen
            precio en la lista {datos.listaPrecio}, así que valorizan en cero y el total está por
            debajo del real. Cargales el precio en SAP para que el número cierre.
          </div>
        </div>
      )}

      {/* --- Valor del stock por proveedor --- */}
      <div className="card shadow-sm mt-2">
        <div className="card-body">
          <h5 className="card-title mb-1">Valor del stock en consignación por proveedor</h5>
          <p className="text-muted small">
            Valorizado con el precio de la lista {datos.listaPrecio}, la misma con la que sale la
            orden de reposición. Es mercadería del proveedor hasta que se consume: no está pagada,
            pero mide la exposición con cada uno.
          </p>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Proveedor</th>
                  <th className="text-end">Artículos</th>
                  <th className="text-end">Unidades</th>
                  <th className="text-end">Valorizado</th>
                  <th className="text-end">Consumo</th>
                  <th className="text-end">Sin movimiento</th>
                </tr>
              </thead>
              <tbody>
                {datos.proveedores.map(p => (
                  <tr key={p.cardCode}>
                    <td>
                      {p.cardName}
                      <code className="text-muted small ms-2">{p.cardCode}</code>
                    </td>
                    <td className="text-end">{formatNumber(p.articulos)}</td>
                    <td className="text-end">{formatNumber(p.unidades)}</td>
                    <td className="text-end fw-semibold">{formatMoney(p.valorizado)}</td>
                    <td className="text-end">{formatNumber(p.consumo)}</td>
                    <td className="text-end">
                      {p.sinMovimiento > 0
                        ? <span className="text-muted">{p.sinMovimiento} de {p.articulos}</span>
                        : <span className="text-success">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- Calibración del stock mínimo --- */}
      {(datos.sugerencias.length > 0 || datos.calibracion) && (
        <div className="card shadow-sm mt-3">
          <div className="card-body">
            <h5 className="card-title mb-1">Calibración del stock mínimo</h5>
            {/* Las dos ventanas no son simétricas y conviene decirlo acá: si no,
                parece un error que un artículo flojo este mes no aparezca para
                bajar. */}
            <p className="text-muted small mb-2">
              <strong>Subir</strong> salta por dos motivos: que en los últimos{' '}
              {datos.calibracion?.ventanaSuba ?? 30} días haya salido más de lo que el mínimo
              cubre, o que el artículo salga una y otra vez y su ritmo promedio ya lo supere.
              Cuando aplican los dos manda el número más alto.{' '}
              <strong>Bajar</strong> exige {datos.calibracion?.ventanaBaja ?? 120} días
              (4 meses) de poca rotación: un mes flojo no alcanza para apostar a que el
              repuesto no se va a necesitar. Los que no se movieron en esos 4 meses
              se proponen en cero. El cambio se aplica en SAP, no acá.
            </p>

            {/* Sin cuatro meses de datos NO se sugiere bajar nada: con el
                almacén recién abierto todo parece de baja rotación. Decirlo
                evita leer la lista vacía como si fuera un error. */}
            {datos.calibracion && !datos.calibracion.puedeSugerirBajas && (
              <div className="alert alert-light border py-2 px-3 small">
                <i className="bi bi-info-circle me-1"></i>
                El almacén lleva {formatNumber(datos.calibracion.historiaDias)} días de
                historia. Hasta llegar a {datos.calibracion.ventanaBaja} no se sugiere
                bajar ningún mínimo: recién arrancado, todo parece de baja rotación
                porque todavía no le tocó salir.
              </div>
            )}

            {datos.sugerencias.length === 0 && (
              <p className="text-muted small fst-italic mb-0">
                No hay mínimos para ajustar con los datos de hoy.
              </p>
            )}
            {datos.sugerencias.length > 0 && (<>
            <div className="tabla-consignacion">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th style={{ width: '100px' }}>Código</th>
                    <th>Artículo</th>
                    <th className="text-end" style={{ width: '90px' }}>Consumo</th>
                    {/* Con qué ventana se decidió: sin esto, un "bajar" basado en
                        4 meses parece contradecir el consumo del período. */}
                    <th className="text-end" style={{ width: '90px' }}>Ventana</th>
                    <th className="text-end" style={{ width: '80px' }}>Mín. hoy</th>
                    <th className="text-end" style={{ width: '90px' }}>Sugerido</th>
                    <th style={{ width: '120px' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.sugerencias.map(s => {
                    const tipo = SUGERENCIAS[s.sugerencia.tipo] || {};
                    return (
                      <tr key={s.codigo} title={s.sugerencia.motivo}>
                        <td><code className="small">{s.codigo}</code></td>
                        <td className="small celda-descripcion">{s.descripcion}</td>
                        <td className="text-end">{formatNumber(s.consumo)}</td>
                        <td className="text-end text-muted small text-nowrap">
                          {s.sugerencia.ventanaDias} días
                        </td>
                        <td className="text-end text-muted">{formatNumber(s.stockMinimo)}</td>
                        <td className="text-end fw-bold">{formatNumber(s.sugerencia.sugerido)}</td>
                        <td>
                          <span className={`badge fw-normal ${tipo.clase || ''}`}>
                            <i className={`bi ${tipo.icono} me-1`}></i>
                            {tipo.texto}
                          </span>
                          {/* Una baja por cero consumo no es lo mismo que una
                              calculada sobre un promedio: conviene mirarla antes
                              de vaciar el mínimo. */}
                          {s.sugerencia.sinConsumo && (
                            <span className="d-block text-muted" style={{ fontSize: '.72rem' }}>
                              sin rotación
                            </span>
                          )}
                          {/* Distingue el pico de un mes del artículo que sale
                              todo el tiempo: son dos problemas distintos. */}
                          {s.sugerencia.reiterado && (
                            <span className="d-block text-muted" style={{ fontSize: '.72rem' }}>
                              consumo reiterado
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>)}
          </div>
        </div>
      )}

      {/* --- Rotación --- */}
      {datos.hayMovimientos && (
        <div className="row mt-3">
          <div className="col-lg-7 mb-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h5 className="card-title mb-3">Más consumidos</h5>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Artículo</th>
                        <th className="text-end">Consumo</th>
                        <th className="text-end">Stock</th>
                        <th className="text-end">Cobertura</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.masConsumidos.map(a => (
                        <tr key={a.codigo}>
                          <td className="small">
                            <code>{a.codigo}</code>
                            <span className="d-block text-muted celda-descripcion">{a.descripcion}</span>
                          </td>
                          <td className="text-end fw-semibold">{formatNumber(a.consumo)}</td>
                          <td className="text-end text-muted">{formatNumber(a.stock)}</td>
                          <td className="text-end">
                            {a.diasCobertura === null
                              ? '—'
                              : <span className={a.diasCobertura < 15 ? 'text-danger fw-semibold' : ''}>
                                  {formatNumber(a.diasCobertura)} días
                                </span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-5 mb-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h5 className="card-title mb-1">Consumo por destino</h5>
                <p className="text-muted small">
                  A qué almacén fue a parar el stock. Se sabe porque el consumo es un traslado.
                </p>
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Almacén</th>
                      <th className="text-end">Unidades</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.destinos.map(d => (
                      <tr key={d.almacen}>
                        <td><code>{d.almacen}</code></td>
                        <td className="text-end fw-semibold">{formatNumber(d.unidades)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Sin movimiento --- */}
      {datos.sinMovimiento.length > 0 && (
        <div className="card shadow-sm mt-1">
          <div className="card-body">
            <h5 className="card-title mb-1">
              Sin movimiento en el período
              <span className="text-muted fs-6 fw-normal ms-2">({totales.sinMovimiento})</span>
            </h5>
            <p className="text-muted small">
              Ordenados por valor del stock. Un artículo que no rota no debería ocupar
              lugar en consignación{!datos.hayMovimientos && ', aunque con el almacén sin movimientos esto todavía no dice nada'}.
            </p>
            <div className="tabla-consignacion">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th style={{ width: '100px' }}>Código</th>
                    <th>Artículo</th>
                    <th>Proveedor</th>
                    <th className="text-end" style={{ width: '80px' }}>Stock</th>
                    <th className="text-end" style={{ width: '120px' }}>Valorizado</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.sinMovimiento.map(a => (
                    <tr key={a.codigo}>
                      <td><code className="small">{a.codigo}</code></td>
                      <td className="small celda-descripcion">{a.descripcion}</td>
                      <td className="small text-muted">{a.proveedorNombre}</td>
                      <td className="text-end">{formatNumber(a.stock)}</td>
                      <td className="text-end">{formatMoney(a.valorizado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabInforme;
